import { useCallback, useEffect, useRef, useState } from 'react';
import { studioApi, idempotencyKey } from '../../../data/studioApi';
import { uploadAsset } from '../../../data/uploads';
import { STUDIO_INBOX, conversationPath } from '../../../enums/routes';

const POLL_MS = 20000;
const PER_PAGE = 50;
const BATCH = 30;

// The Direct mirrored by core (contract 2.14.0, 05): the conversations of the active profile, one
// open, its messages in batches. Opening marks it read, and a reply stays on screen while it goes.
export default function useInbox(s) {
  const profileId = s.profile?.id;
  const { pathname } = s.location;
  const { refresh: refreshOverview } = s.automations;
  const openId = pathname.match(/^\/studio\/inbox\/(scv-[^/]+)/)?.[1] || null;
  const [conversations, setConversations] = useState(null);
  const [status, setStatus] = useState('loading');
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [thread, setThread] = useState({ id: null, conversation: null, messages: [], cursor: null, more: false, state: 'idle' });
  const [olderLoading, setOlderLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const live = useRef(true);
  useEffect(() => () => { live.current = false; }, []);
  const listReads = useRef(0);

  const loadList = useCallback(async () => {
    if (!profileId) return;
    const read = ++listReads.current;
    try {
      const page = await studioApi.conversations({ profile_id: profileId, unread: onlyUnread || undefined, per_page: PER_PAGE });
      if (!live.current || read !== listReads.current) return;
      setConversations(page.data);
      setStatus('ready');
    } catch (failure) {
      if (live.current && read === listReads.current) setStatus(current => (failure.code === 'connection_required' ? 'no_connection' : current === 'ready' ? current : 'error'));
    }
  }, [profileId, onlyUnread]);
  useEffect(() => { setStatus('loading'); setConversations(null); loadList(); }, [loadList]);

  const openThread = useCallback(async (id, { quiet = false } = {}) => {
    if (!quiet) setThread({ id, conversation: null, messages: [], cursor: null, more: false, state: 'loading' });
    try {
      const [conversation, batch] = await Promise.all([studioApi.conversation(id), studioApi.directMessages(id, { limit: BATCH })]);
      if (!live.current) return;
      // The API answers newest first; the thread reads top to bottom. A quiet read keeps the older
      // messages already loaded above this batch.
      const newest = [...batch.data].reverse();
      setThread(current => {
        if (quiet && current.id !== id) return current;
        const ids = new Set(newest.map(message => message.id));
        const older = quiet ? current.messages.filter(message => !ids.has(message.id)) : [];
        return { id, conversation, messages: [...older, ...newest], cursor: older.length ? current.cursor : batch.next_cursor, more: older.length ? current.more : batch.has_more, state: 'ready' };
      });
      if (conversation.unread_count > 0) {
        const read = await studioApi.updateConversation(id, { read: true }, idempotencyKey('conversation-read'));
        if (!live.current) return;
        setThread(current => (current.id === id ? { ...current, conversation: read } : current));
        setConversations(list => list?.map(item => (item.id === id ? read : item)) ?? list);
        refreshOverview();
      }
    } catch { if (live.current && !quiet) setThread(current => ({ ...current, state: 'error' })); }
  }, [refreshOverview]);
  useEffect(() => { if (openId) openThread(openId); else setThread(current => ({ ...current, id: null, state: 'idle' })); }, [openId, openThread]);

  // No push in this contract: the page reads again while it is visible.
  useEffect(() => {
    const timer = setInterval(() => {
      if (document.hidden) return;
      loadList();
      if (openId) openThread(openId, { quiet: true });
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [loadList, openId, openThread]);

  const loadOlder = useCallback(async () => {
    if (!thread.cursor) return;
    setOlderLoading(true);
    try {
      const batch = await studioApi.directMessages(thread.id, { before: thread.cursor, limit: BATCH });
      if (live.current) setThread(current => ({ ...current, messages: [...[...batch.data].reverse(), ...current.messages], cursor: batch.next_cursor, more: batch.has_more }));
    } catch { /* The button stays, to try again. */ } finally { if (live.current) setOlderLoading(false); }
  }, [thread.cursor, thread.id]);

  // A manual reply also cancels what the automation still had to say here (core's rule).
  const send = useCallback(async ({ text, image }) => {
    const id = thread.id;
    setSending(true);
    setSendError(null);
    try {
      const asset = image ? await uploadAsset({ purpose: 'art_image', profile_id: profileId, mime_type: image.type }, image) : null;
      const body = { ...(text ? { text } : {}), ...(asset ? { asset_id: asset.id } : {}) };
      const message = await studioApi.sendDirectMessage(id, body, idempotencyKey('direct-message'));
      if (live.current) setThread(current => (current.id === id ? { ...current, messages: [...current.messages, message] } : current));
      loadList();
      return true;
    } catch (failure) {
      if (live.current) {
        setSendError(failure.code);
        // The window closed while the conversation was open: read what it says now.
        if (failure.code === 'messaging_window_closed') openThread(id, { quiet: true });
      }
      return false;
    } finally { if (live.current) setSending(false); }
  }, [loadList, openThread, profileId, thread.id]);

  return { status, conversations, onlyUnread, setOnlyUnread, openId, thread, olderLoading, loadOlder, sending, sendError, send, reload: loadList,
    open: id => s.navigate(conversationPath(id)), close: () => s.navigate(STUDIO_INBOX) };
}
